import { shortTime } from "./calls";
import { memberName } from "./memberNames";

export const NOTE_MAX = 2000;

export const REASONS = ["service", "container", "sales", "billing", "safety", "other"] as const;
export type IssueReason = (typeof REASONS)[number];

export const REASON_TEXT: Record<IssueReason, string> = {
  service: "Service (a missed or late stop)",
  container: "Container",
  sales: "Sales must check",
  billing: "Billing",
  safety: "Safety",
  other: "Other",
};

/** Design 8.10: the sensor rule that opened an issue. */
export type IssueRule = "stuck" | "theft" | "spike" | "silence";

export const RULE_TEXT: Record<IssueRule, string> = {
  stuck: "Sensor: stuck",
  theft: "Sensor: possible theft",
  spike: "Sensor: spike",
  silence: "Sensor: silence",
};

/** The title of an issue: its sensor rule, or else its reason. */
export function issueTitle(issue: { reason: IssueReason; rule?: IssueRule | null }): string {
  return issue.rule ? RULE_TEXT[issue.rule] : REASON_TEXT[issue.reason];
}

export const RESOLUTIONS = ["fixed", "no_fault", "duplicate", "other"] as const;
export type Resolution = (typeof RESOLUTIONS)[number];

export const RESOLUTION_TEXT: Record<Resolution, string> = {
  fixed: "Fixed",
  no_fault: "No fault",
  duplicate: "Duplicate",
  other: "Other",
};

/** Spec 3.11 F-QC-4: these resolutions need a note. */
export const NOTE_NEEDED: ReadonlySet<Resolution> = new Set<Resolution>(["no_fault", "other"]);

export type IssueStatus = "assigned" | "acknowledged" | "resolved" | "closed";
export type StepKind = "acknowledge" | "reassign" | "resolve" | "close" | "reopen";

export const STATUS_TEXT: Record<IssueStatus, string> = {
  assigned: "Assigned",
  acknowledged: "Acknowledged",
  resolved: "Resolved",
  closed: "Closed",
};

export const STEP_TEXT: Record<StepKind, string> = {
  acknowledge: "Acknowledge",
  reassign: "Reassign",
  resolve: "Resolve",
  close: "Close the issue",
  reopen: "Reopen",
};

const DONE_TEXT: Record<StepKind, string> = {
  acknowledge: "Acknowledged",
  reassign: "Reassigned",
  resolve: "Resolved",
  close: "Closed",
  reopen: "Reopened",
};

/** The status line after a step, which names the issue. */
export function stepDoneText(
  kind: StepKind,
  issue: { reason: IssueReason; rule?: IssueRule | null; account_number: string },
  assignee?: string,
): string {
  const to = kind === "reassign" && assignee ? ` to ${assignee}` : "";
  return `${DONE_TEXT[kind]}${to}: ${issueTitle(issue)} on ${issue.account_number}.`;
}

export interface HistoryEvent {
  kind: "open" | StepKind;
  resolution: Resolution | null;
  assignee_email: string | null;
}

/** One line of the history of an issue, without who and when. */
export function historyText(event: HistoryEvent): string {
  if (event.kind === "open") return event.assignee_email ? `Opened, assigned to ${memberName(event.assignee_email)}` : "Opened";
  if (event.kind === "reassign") return event.assignee_email ? `Reassigned to ${memberName(event.assignee_email)}` : "Reassigned";
  if (event.kind === "resolve" && event.resolution) return `Resolved: ${RESOLUTION_TEXT[event.resolution]}`;
  return DONE_TEXT[event.kind];
}

interface Ordered {
  status: IssueStatus;
  due_date: string | null;
  opened_at: string;
  updated_at: string;
}

/** A due date before today on an issue that nobody has resolved yet. */
export function isOverdue(issue: Pick<Ordered, "status" | "due_date">, today: string): boolean {
  const waiting = issue.status === "assigned" || issue.status === "acknowledged";
  return waiting && issue.due_date !== null && issue.due_date < today;
}

/** Open issues first: the nearest due date, then the oldest. Closed issues last, the newest change first. */
export function byUrgency(a: Ordered, b: Ordered): number {
  const closedA = a.status === "closed";
  const closedB = b.status === "closed";
  if (closedA !== closedB) return closedA ? 1 : -1;
  if (closedA) return b.updated_at.localeCompare(a.updated_at);
  if (a.due_date !== b.due_date) {
    if (a.due_date === null) return 1;
    if (b.due_date === null) return -1;
    return a.due_date.localeCompare(b.due_date);
  }
  return a.opened_at.localeCompare(b.opened_at);
}

export type IssueView = "mine" | "all";

/** The tab of the Issues page. A role that nobody can assign an issue to starts on all issues. */
export function defaultView(search: string, canTake: boolean | undefined): IssueView {
  const asked = new URLSearchParams(search).get("view");
  if (asked === "all" || asked === "mine") return asked;
  return canTake === false ? "all" : "mine";
}

const FROM: Record<StepKind, IssueStatus[]> = {
  acknowledge: ["assigned"],
  reassign: ["assigned", "acknowledged", "resolved"],
  resolve: ["assigned", "acknowledged"],
  close: ["resolved"],
  reopen: ["closed"],
};

/** The steps that the user can take now. Only the assignee acknowledges (F-QC-3). */
export function nextSteps(status: IssueStatus, canEdit: boolean, mine: boolean): StepKind[] {
  if (!canEdit) return [];
  return (Object.keys(FROM) as StepKind[]).filter(
    (kind) => FROM[kind].includes(status) && (kind !== "acknowledge" || mine),
  );
}

export function resolveError(resolution: Resolution | "", note: string): string {
  if (!resolution) return "Choose how the issue was resolved.";
  if (NOTE_NEEDED.has(resolution) && !note.trim()) return "This resolution needs a note.";
  if (note.trim().length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  return "";
}

export function openError(assignee: string, note: string, due: string, today: string): string {
  if (!assignee) return "Choose who takes the issue.";
  if (note.trim().length > NOTE_MAX) return `The note can have ${NOTE_MAX} characters at most.`;
  if (due && due < today) return "The due date cannot be before today.";
  return "";
}

export type IssueField = "assignee" | "resolution" | "note" | "due";

/** The field that an error of openError or resolveError is about, so that it shows next to it. */
export function issueField(error: string): IssueField {
  if (error.includes("note")) return "note";
  if (error.includes("due date")) return "due";
  if (error.includes("resolved")) return "resolution";
  return "assignee";
}

/** Whole days from the open time to now, as F-QC-5 shows them. */
export function daysOpen(openedAt: string, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(openedAt).getTime()) / 86_400_000));
}

export function daysOpenText(days: number): string {
  return days === 1 ? "1 day open" : `${days} days open`;
}

/** 080 C5: "Customer acknowledged Oct 12, 2:05 PM". The issue keeps its own status. */
export function customerAckText(at: string, timeZone: string): string {
  return `Customer acknowledged ${shortTime(at, timeZone)}`;
}
